import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../auth/auth.service';
import { EditFeeDialog } from '../edit-fee-dialog/edit-fee-dialog';
import { StockService } from '../stock.service';
import { PaymentService } from '../../payment/payment.service';
import { Stock, TransferRun } from '../stock.type';

/**
 * Per-account transfer history.
 *
 * Each row is one durable run, not a snapshot of the account. A failed run still
 * reports the coins it actually moved, and each carries the fee it snapshotted
 * when it was claimed — which is why editing the account's fee later cannot
 * rewrite what is shown here.
 */
@Component({
  selector: 'app-stock-runs-dialog',
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatTableModule, MatProgressSpinnerModule, MatCheckboxModule, MatTooltipModule],
  template: `
    <h2 mat-dialog-title>Transfer history</h2>
    <mat-dialog-content>
      <p class="account">{{ data.stock.login_info_email }}</p>
      <mat-checkbox [(ngModel)]="includeZeroCoinRuns" (change)="reload()">
        Show runs that transferred 0 coins
      </mat-checkbox>

      @if (loading) {
      <div class="center"><mat-spinner diameter="28"></mat-spinner></div>
      } @else if (error) {
      <div class="center err">{{ error }}</div>
      } @else if (runs.length === 0) {
      <div class="center">No transfer runs recorded for this account yet.</div>
      } @else {
      <div class="table-wrap">
        <table mat-table [dataSource]="runs">
          <ng-container matColumnDef="confirmed">
            <th mat-header-cell *matHeaderCellDef>Confirmed</th>
            <td mat-cell *matCellDef="let r">{{ (r.confirmed_at || r.started_at) | date:'short' }}</td>
          </ng-container>
          <ng-container matColumnDef="coinRange">
            <th mat-header-cell *matHeaderCellDef>Start &rarr; end coin</th>
            <td mat-cell *matCellDef="let r">{{ r.start_coins | number }} &rarr; {{ r.last_coins | number }}</td>
          </ng-container>
          <ng-container matColumnDef="coins">
            <th mat-header-cell *matHeaderCellDef>Coins moved</th>
            <td mat-cell *matCellDef="let r">{{ r.coins_transferred | number }}</td>
          </ng-container>
          <ng-container matColumnDef="fee">
            <th mat-header-cell *matHeaderCellDef>Fee snapshot</th>
            <td mat-cell *matCellDef="let r"><span class="inline-actions">
                {{ r.fee_snapshot | number }}
                @if (canEditFee && r.payment_status !== 'Payed') {
                <button mat-icon-button class="mini-btn" matTooltip="Edit fee snapshot" (click)="openEditFeeDialog(r)">
                  <mat-icon>edit</mat-icon>
                </button>
                }
              </span>
            </td>
          </ng-container>
          <ng-container matColumnDef="payable">
            <th mat-header-cell *matHeaderCellDef>Payable</th>
            <td mat-cell *matCellDef="let r">{{ r.payable_amount | number }}</td>
          </ng-container>
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let r">
              {{ r.status }}
              @if (r.failed_reason) { <div class="sub">{{ r.failed_reason }}</div> }
            </td>
          </ng-container>
          <ng-container matColumnDef="payment">
            <th mat-header-cell *matHeaderCellDef>Payment</th>
            <td mat-cell *matCellDef="let r">{{ r.payment_status }}</td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="cols"></tr>
          <tr mat-row *matRowDef="let row; columns: cols;"></tr>
        </table>
      </div>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .account { font-family: monospace; font-size: 12px; opacity: .8; }
    .table-wrap { overflow-x: auto; }
    table { width: 100%; }
    .center { padding: 32px; text-align: center; opacity: .7; }
    .center.err { color: #c62828; }
    .inline-actions { display: inline-flex; align-items: center; gap: 4px; }
    .mini-btn { transform: scale(.75); }
    .sub { font-size: 11px; color: #c62828; max-width: 220px; }
  `],
})
export class StockRunsDialog implements OnInit {
  cols = ['confirmed', 'coinRange', 'coins', 'fee', 'payable', 'status', 'payment'];
  runs: TransferRun[] = [];
  loading = true;
  error: string | null = null;
  /** Admin-only toggle: suppliers never see zero-coin runs regardless of this. */
  includeZeroCoinRuns = false;

  constructor(
    private stockService: StockService,
    private paymentService: PaymentService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: { stock: Stock },
  ) { }

  get canEditFee(): boolean {
    return this.authService.hasAnyRole('Admin', 'SuperAdmin') && this.authService.canWrite('suppliers_payments');
  }

  openEditFeeDialog(run: TransferRun): void {
    this.dialog.open(EditFeeDialog, {
      width: '360px',
      maxWidth: '95vw',
      data: { runLabel: run.login_info_email || run.id.slice(0, 8), feeSnapshot: run.fee_snapshot },
    }).afterClosed().subscribe((feeSnapshot: number | null) => {
      if (feeSnapshot === null || feeSnapshot === undefined) return;
      this.paymentService.updateTransferRunFee(run.id, feeSnapshot).subscribe({
        next: () => {
          this.snackBar.open('Fee snapshot updated', 'Close', { duration: 4000 });
          this.reload();
        },
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed to update fee', 'Close', { duration: 6000 }),
      });
    });
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading = true;
    this.error = null;
    this.stockService.getTransferRuns({
      stockId: this.data.stock.id,
      pageSize: 100,
      includeZeroCoinRuns: this.includeZeroCoinRuns,
    }).subscribe({
      next: res => { this.runs = res.runs; this.loading = false; },
      error: err => { this.error = err?.error?.error ?? 'Failed to load runs'; this.loading = false; },
    });
  }
}
